import { ErrorHandler, inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ZodError } from 'zod';
import { ErrorService } from '../services/error.service';
import { AuthService } from '../services/auth.service';
import { extractErrorDetails } from '../utils/errorUtils';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
    private readonly errorService = inject(ErrorService);
    private readonly authService = inject(AuthService);

    handleError(error: unknown): void {
        const unwrapped = this.unwrapError(error);

        if (unwrapped instanceof HttpErrorResponse) {
            if (unwrapped.status === 401) {
                return;
            }

            // Skip guest-specific 403s - already handled by auth interceptor (toast or modal)
            if (unwrapped.status === 403) {
                const code = unwrapped.error?.detail?.code;
                if (code === 'guest_upgrade_required' || code === 'guest_limit_reached' || code === 'guest_session_expired') {
                    return;
                }
            }

            const serverMessage = extractErrorDetails(unwrapped);
            this.errorService.showHttpError(unwrapped.status, serverMessage ?? undefined);
            return;
        }

        if (unwrapped instanceof ZodError) {
            const formatted = unwrapped.format();
            const errorMessage = `API Response Validation Failed: ${JSON.stringify(formatted, null, 2)}`;
            const stackTrace = unwrapped.stack ?? '';

            console.error(errorMessage);
            this.errorService.logToBackend(errorMessage, stackTrace, this.authService.state().user);
            return;
        }

        console.error('Unhandled error:', unwrapped);
    }

    private unwrapError(error: unknown): unknown {
        if (error && typeof error === 'object' && 'rejection' in error) {
            return (error as { rejection: unknown }).rejection;
        }
        return error;
    }
}
